<template>
    <div class="relative" v-click-outside="() => (isOpen = false)">
        <button
            @click.prevent="toggleDropdown"
            @keydown.enter="toggleDropdown"
            @keydown.space.prevent="toggleDropdown"
            :disabled="disabled"
            type="button"
            tabindex="0"
            class="font-medium whitespace-nowrap rounded shadow border flex items-center justify-between gap-x-1.5 bg-white px-3 py-2 text-sm text-slate-800 hover:border-slate-800 focus:outline focus:border-slate-800 focus:outline-none disabled:opacity-50 disabled:text-slate-800 disabled:bg-white disabled:border-slate-300 w-full"
        >
            <span class="flex items-center gap-2">
                <div>{{ label }}</div>
                <div
                    v-if="showCounter"
                    class="px-1.5 py-0.5 bg-slate-800 rounded-[999px] flex-col justify-center items-center gap-2.5 flex text-white text-xs font-medium leading-none w-4"
                    :class="{
                        'xl:invisible max-xl:hidden':
                            selectedOptions.length === 0,
                    }"
                >
                    {{ selectedOptions.length }}
                </div>
            </span>
            <Icon icon="chevron-down" class="w-4 h-4" />
        </button>
        <div
            v-if="isOpen"
            :class="dropdownClass"
            class="min-w-max"
            style="transform: translate3d(0, 0, 200px)"
        >
            <div
                class="px-4 py-2.5 bg-white rounded-lg justify-start items-center gap-2 flex w-full text-slate-800"
            >
                <Icon icon="search" />
                <div class="w-full">
                    <label for="autosuggest" class="sr-only">
                        {{ placeholder }}
                    </label>
                    <input
                        id="autosuggest"
                        type="text"
                        :placeholder="placeholder"
                        v-model="userInput"
                        @input="handleInput"
                        v-debounce="debounceTime"
                        @keydown.down="navigate(1)"
                        @keydown.up="navigate(-1)"
                        @keydown.enter.prevent="handleSubmit"
                        class="p-0 border-none w-full h-full focus:ring-0 relative text-sm font-normal leading-tight placeholder:text-slate-500 placeholder:font-normal"
                        aria-controls="options"
                        aria-expanded="false"
                        autocomplete="off"
                    />
                </div>
            </div>
            <div class="w-full h-px bg-slate-200"></div>
            <slot name="options">
                <div class="max-h-[200px] overflow-y-auto">
                    <ul id="options" role="listbox" class="p-2">
                        <li
                            v-if="mode === 'single'"
                            v-for="(suggestion, index) in filteredSuggestions"
                            :key="suggestion"
                            :class="[
                                optionClass,
                                { 'bg-slate-100': index === selectedIndex },
                            ]"
                            tabindex="-1"
                            role="option"
                            @click="handleSingleSelect(index)"
                        >
                            <div
                                class="grow shrink basis-0 flex-col justify-center items-start gap-1 flex"
                            >
                                <div
                                    class="text-slate-800 text-sm font-normal leading-tight"
                                >
                                    {{ suggestion[displayKey] }}
                                </div>
                            </div>
                        </li>
                        <li
                            v-if="mode === 'multiple'"
                            v-for="(suggestion, index) in filteredSuggestions"
                            :key="`suggestion-${index}`"
                            :class="[
                                optionClass,
                                { 'bg-slate-100': index === selectedIndex },
                            ]"
                            role="option"
                            tabindex="-1"
                        >
                            <input
                                type="checkbox"
                                class="py-0.5 justify-start items-center flex text-orange-600 w-4 h-4 bg-white rounded border border-slate-300 focus:ring-0 focus:ouline-none hover:bg-slate-100 cursor-pointer"
                                :id="suggestion[valueKey]"
                                :name="suggestion[displayKey]"
                                :checked="isSelected(suggestion)"
                                @change="handleMultiSelect(index)"
                            />
                            <label
                                :for="suggestion[valueKey]"
                                :class="labelClass"
                            >
                                <p>{{ suggestion[displayKey] }}</p>
                            </label>
                        </li>
                    </ul>
                </div>
                <p
                    v-if="noResultsFound"
                    class="p-3 bg-white rounded-lg text-slate-800 text-sm leading-tight"
                >
                    No results found, try adjusting your search
                </p>
            </slot>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    emits: ['toggleSelect', 'clear', 'filter'],
    props: {
        placeholder: {
            type: String,
            required: false,
        },
        preloadData: {
            type: Boolean,
            default: false,
        },
        preloadedOptions: {
            type: Array,
            required: false,
            default: [],
        },
        label: {
            type: String,
            default: 'Select options...',
        },
        showCounter: {
            type: Boolean,
            default: false,
        },
        displayKey: {
            type: String,
            default: 'name',
        },
        valueKey: {
            type: String,
            default: 'value',
        },
        mode: {
            type: String,
            default: 'single',
        },
        selected: {
            type: Object,
            required: false,
        },
        selectedOptions: {
            type: Array,
            required: false,
        },
        labelClass: {
            type: String,
            default: 'cursor-pointer hover:bg-slate-100',
        },
        dropdownClass: {
            type: String,
            default:
                'absolute mt-1 border border-slate-200 shadow-sm rounded-lg bg-white z-10',
        },
        optionClass: {
            type: String,
            default:
                'p-2 rounded justify-start items-center flex gap-2 text-slate-800 text-sm font-normal leading-tight cursor-pointer hover:bg-slate-100',
        },
        resultsArrayKey: {
            type: String,
            default: 'data',
        },
        url: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    components: { Icon },
    setup(props, { emit }) {
        const isOpen = ref(false);
        const userInput = ref('');
        const selectedIndex = ref(-1);
        const fetchedOptions = ref([]);
        const filteredSuggestions = computed(() => {
            if (props.preloadData) {
                if (userInput.value) {
                    return props.preloadedOptions.filter((option) => {
                        return option[props.displayKey]
                            .toLowerCase()
                            .includes(userInput.value.toLowerCase());
                    });
                } else {
                    return props.preloadedOptions;
                }
            } else {
                return fetchedOptions.value;
            }
        });

        const isValid = computed(() => {
            return filteredSuggestions.value.length === 1;
        });

        const noResultsFound = computed(() => {
            return filteredSuggestions.value.length === 0;
        });

        const debounceTime = computed(() => {
            return props.preloadData ? 100 : 600;
        });

        const toggleDropdown = () => {
            isOpen.value = !isOpen.value;
        };

        const handleSingleSelect = (option) => {
            if (props.mode === 'single') {
                emit('toggleSelect', option);
                isOpen.value = false;
            }
        };

        const isSelected = (option) => {
            return props.selectedOptions.some(
                (item) => item[props.valueKey] === option[props.valueKey]
            );
        };

        const handleMultiSelect = (index = selectedIndex.value) => {
            if (props.mode === 'multiple') {
                if (index >= 0 && index < filteredSuggestions.value.length) {
                    emit('toggleSelect', filteredSuggestions.value[index]);
                }
            }
        };

        const handleInput = (e) => {
            selectedIndex.value = -1;
            if (props.preloadData) {
                isOpen.value = true;
                selectedIndex.value = 0;
            } else if (
                userInput.value.length > 3 ||
                userInput.value.length === 0
            ) {
                getItems();
                isOpen.value = true;
                selectedIndex.value = 0;
            }
            emit('filter', userInput.value);
        };

        const getItems = async () => {
            try {
                const resp = await ApiService.apiCall(
                    `${props.url}${userInput.value}`,
                    'get'
                );
                fetchedOptions.value = resp.data[props.resultsArrayKey];
                selectedIndex.value = 0;
            } catch (error) {
                console.error(error);
            }
        };

        const handleSubmit = () => {
            if (selectedIndex.value >= 0) {
                if (props.mode === 'single') {
                    handleSingleSelect(
                        filteredSuggestions.value[selectedIndex.value]
                    );
                } else {
                    handleMultiSelect(selectedIndex.value);
                }
            }
        };

        const navigate = (direction) => {
            if (!filteredSuggestions.value.length) return;
            selectedIndex.value =
                (selectedIndex.value +
                    direction +
                    filteredSuggestions.value.length) %
                filteredSuggestions.value.length;
        };

        watch(
            () => props.selected,
            () => {
                if (props.mode === 'single') {
                    userInput.value = props.selected[props.displayKey];
                }
            },
            {
                immediate: true,
            }
        );

        watch(isValid, () => {
            if (isValid.value) {
                selectedIndex.value = 0;
            }
        });

        onMounted(() => {
            if (!props.preloadData) {
                getItems();
            }
        });

        return {
            filteredSuggestions,
            debounceTime,
            handleInput,
            handleMultiSelect,
            handleSingleSelect,
            handleSubmit,
            isOpen,
            isSelected,
            navigate,
            noResultsFound,
            selectedIndex,
            toggleDropdown,
            userInput,
        };
    },
};
</script>
